/* Install-location lookup for Dongar adapter SKUs.
   Tells shoppers WHERE on the vehicle each adapter installs and the trade-off
   between options when multiple SKUs match the same vehicle.
   Descriptions are deliberately vehicle-agnostic — many SKUs span multiple
   makes/models, so the install-location text describes the physical port
   only, not vehicle-specific install steps.
*/
(function () {
  'use strict';

  // type values are used to detect when ≥2 *different* install types are
  // among the matched SKUs (so we know it's worth surfacing the comparison).
  //   domelight       — overhead console / map-light cavity
  //   mirror_back     — port directly behind the auto-dimming mirror (no shroud)
  //   mirror_shroud   — port at base of mirror, under the plastic shroud
  //   rainsensor      — windshield rainsensor module
  //   tss             — Toyota Safety Sense lane-trace assist computer
  //   combo           — bundles multiple harnesses
  var BY_SKU = {
    // ── Domelight / overhead-console ports ────────────────────────────────
    'DCA-5A': { type: 'domelight', label: 'Domelight install',
      summary: 'Hidden in the overhead console — same port the manufacturer uses for their dealer-installed dash cam.' },
    'DCA-5B': { type: 'domelight', label: 'Domelight install',
      summary: 'Hidden in the overhead console — same port the manufacturer uses for their dealer-installed dash cam.' },
    'DCA-5C': { type: 'domelight', label: 'Domelight install',
      summary: 'Hidden in the overhead console — manufacturer OEM dashcam port (newer connector revision).' },
    'DCA-8B': { type: 'domelight', label: 'Domelight install',
      summary: 'Hidden in the overhead console — same port used to power the maplight.' },

    // ── Mirror, port directly behind (no shroud removal) ──────────────────
    'DCA-7A':  { type: 'mirror_back', label: 'Autodimming Rearview Mirror install',
      summary: 'Port located directly behind the auto-dimming mirror — no shroud removal needed.' },
    'DCA-7B':  { type: 'mirror_back', label: 'Autodimming Rearview Mirror install',
      summary: 'Port located directly behind the auto-dimming mirror — no shroud removal needed.' },
    'DCA-10A': { type: 'mirror_back', label: 'Autodimming Rearview Mirror install',
      summary: 'Port located directly behind the auto-dimming mirror — no shroud removal needed.' },
    'DCA-12A': { type: 'mirror_back', label: 'Autodimming Rearview Mirror install',
      summary: 'Port located directly behind the auto-dimming mirror — no shroud removal needed.' },
    'DCA-16A': { type: 'mirror_back', label: 'Autodimming Rearview Mirror install',
      summary: 'Port located directly behind the auto-dimming mirror — no shroud removal needed.' },
    'DCA-16B': { type: 'mirror_back', label: 'Autodimming Rearview Mirror install',
      summary: 'Port located directly behind the auto-dimming mirror — no shroud removal needed.' },

    // ── Mirror, port at base under plastic shroud ─────────────────────────
    'DCA-3A':  { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud or accessible from the maplight cavity. Connector exists even if your vehicle didn’t come with an auto-dimming mirror from the factory.' },
    'DCA-3B':  { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-3V':  { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud or accessible from the maplight cavity.' },
    'DCA-3W1': { type: 'mirror_shroud', label: 'Rainsensor / Autodimming Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud or accessible from the maplight cavity.' },
    'DCA-3W2': { type: 'mirror_shroud', label: 'Rainsensor / Autodimming Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud or accessible from the maplight cavity. On Porsche, this connector is sometimes used as the maplight feed.' },
    'DCA-5E':  { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud near the EyeSight module, or accessible from the maplight cavity.' },
    'DCA-5F':  { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-8A':  { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-10B': { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud. USB output sits on the windshield — easy to plug and unplug. Most recommended option if your vehicle has a Homelink (garage-door opener) mirror.' },
    'DCA-10D': { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-10E': { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-10G': { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-10H': { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-10X': { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-12B': { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },
    'DCA-14A': { type: 'mirror_shroud', label: 'Autodimming Rearview Mirror install',
      summary: 'Port at the base of the auto-dimming mirror, under the plastic shroud.' },

    // ── Rainsensor (windshield-mounted) ───────────────────────────────────
    'DCA-3M':  { type: 'rainsensor', label: 'Rainsensor install',
      summary: 'Connects to the rainsensor module mounted to the windshield.' },
    'DCA-4M':  { type: 'rainsensor', label: 'Rainsensor install',
      summary: 'Connects to the rainsensor module mounted to the windshield.' },
    'DCA-3W3': { type: 'rainsensor', label: 'Rainsensor install',
      summary: 'Connects to the rainsensor module mounted to the windshield.' },

    // ── TSS computer (Toyota Safety Sense) ────────────────────────────────
    'DCA-12T': { type: 'tss', label: 'TSS Computer install',
      summary: 'Powers from the Toyota Safety Sense lane-trace assist computer.' },
    'DCA-16T': { type: 'tss', label: 'TSS Computer install',
      summary: 'Powers from the Toyota Safety Sense lane-trace assist computer.' },

    // ── Combo kits (bundle two harnesses) ─────────────────────────────────
    'DCA-FCP': { type: 'combo', label: 'Autodimming Rearview Mirror install (combo kit)',
      summary: 'Combo kit containing both DCA-3B and DCA-5F harnesses. Connects to the auto-dimming rearview mirror.' },
    'DCA-LRS': { type: 'combo', label: 'Rainsensor install (combo kit)',
      summary: 'Combo kit containing both DCA-3W1 and DCA-3W2 harnesses. Connects to the rainsensor mounted to the windshield.' },

    // ── Parking-mode kits (bundle harnesses for multiple install ports) ───
    'PMA-01':  { type: 'combo', label: 'Parking-mode kit (multi-location install)',
      summary: 'Kit includes a 5-pin harness for the roof cavity (same location as DCA-5A) and a 10-pin harness for the auto-dimming mirror (same location as DCA-10B).' },
    'PROS-01': { type: 'combo', label: 'Parking-mode kit (multi-location install)',
      summary: 'Kit includes a 5-pin harness for the roof cavity (same location as DCA-5A) and a 10-pin harness for the auto-dimming mirror (same location as DCA-10B).' },
    'PMA-02':  { type: 'combo', label: 'Parking-mode kit (Autodimming Rearview Mirror install)',
      summary: 'Kit includes two 10-pin harnesses for the auto-dimming mirror (same connectors as DCA-10A and DCA-10E).' },
    'PROS-02': { type: 'combo', label: 'Parking-mode kit (Autodimming Rearview Mirror install)',
      summary: 'Kit includes two 10-pin harnesses for the auto-dimming mirror (same connectors as DCA-10A and DCA-10E).' },
  };

  // ── Public API ─────────────────────────────────────────────────────────
  window.DongarInstallInfo = {
    lookup: function (sku) {
      if (!sku) return null;
      return BY_SKU[sku] || null;
    },
    // True when ≥2 of the given SKUs have *different* install types — the
    // comparison panel only adds value when the options actually differ.
    hasComparison: function (skus) {
      if (!skus || !skus.length) return false;
      var types = {};
      for (var i = 0; i < skus.length; i++) {
        var info = this.lookup(skus[i]);
        if (info) types[info.type] = true;
      }
      return Object.keys(types).length >= 2;
    },
  };
})();
